"use client"

import clsx from "clsx"
import { motion, AnimatePresence } from "framer-motion"
import type { Variants } from "framer-motion"
import React, { useContext, useEffect } from "react"
import useDimensions from "react-cool-dimensions"

import XOut from "@/../public/icons/x-out.svg"

import { screenSizes } from "@/helpers/utils"
import AppContext from "@/ui/components/AppContext"

import JobFilters from "./JobFilters"
import type { TJobFilters } from "./JobFilters"
import JobFiltersModalTrigger from "./JobFiltersModalTrigger"

// this custom transform template is needed because otherwise Framer will set a translateZ of 0 with the translateY, this caused a rendering bug in Chrome.
// To fix I'm defining a custom transform template, see docs here https://www.framer.com/docs/component/###transformtemplate
function templateX({ x = 0 }) {
  return `translateX(${x})`
}

const setOpenBioVariantX = (currentBreakpoint: string): Variants => {
  let openPercentage
  if (currentBreakpoint === "XL" || currentBreakpoint === "LG") {
    openPercentage = "100%"
  } else if (currentBreakpoint === "MD") {
    openPercentage = "25%"
  } else {
    openPercentage = "0%"
  }

  // Adding display block and none to the open and closed variants respectively
  // This is a quick way to fix the issue where you could see the box shadow of the element on light pages
  // This doesn't impact the animation in any way as: when animating to a non-animatable value like "block", this value will be set instantly.
  return {
    open: {
      x: openPercentage,
      display: "block",
      transition: {
        bounce: 0,
        type: "spring",
      },
    },
    close: {
      x: "100%",
      transition: {
        bounce: 0,
        type: "spring",
      },
      transitionEnd: {
        display: "none",
      },
    },
  }
}

interface JobFiltersModalProps {
  modalOpen: boolean
  filters?: TJobFilters
  currentBreakpoint: string
}

const JobFiltersSlideIn: React.FC<JobFiltersModalProps> = ({
  modalOpen,
  filters = [],
  currentBreakpoint,
}) => {
  const openBioVariantX = setOpenBioVariantX(currentBreakpoint)

  return (
    <AnimatePresence>
      <motion.div
        variants={openBioVariantX}
        transformTemplate={templateX}
        animate={modalOpen ? "open" : "close"}
        exit="close"
        initial="close"
        className="fixed inset-0 z-[10000] translate-x-full lg:hidden"
      >
        <div className="w-screen h-screen p-4 overflow-y-scroll shadow-filters bg-blue-2.5 md:w-3/4">
          <JobFiltersModalTrigger
            modalOpen={false}
            filters={filters}
            className="flex items-center justify-center p-4 mb-3"
          >
            <XOut className="fill-[#141A1F] w-3 h-3" />
          </JobFiltersModalTrigger>
          <div className="space-y-6">
            <JobFilters filters={filters} />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

const ModalBackdrop: React.FC<{
  modalOpen: boolean
  observe: React.Ref<HTMLDivElement> | undefined
}> = ({ modalOpen, observe }) => {
  const context = useContext(AppContext)

  // Have to hardcode the background color to add opacity because of how we've defined colors in :root in _variables.scss.
  // Read Adam Wathan's (creator of Tailwind) explanation here: https://github.com/tailwindlabs/tailwindcss/issues/1692
  return (
    <div
      onMouseUp={() =>
        context.setJobFiltersModal(false, context.jobFiltersModal.filters)
      }
      role="button"
      aria-label="Close mobile job filters modal"
      tabIndex={0}
      ref={observe}
      className={clsx(
        "fixed inset-0 z-[75] w-full h-screen bg-[#141a1f]/20 cursor-default lg:hidden",
        {
          hidden: !modalOpen,
        },
      )}
    />
  )
}

const JobFiltersModal: React.FC<
  Omit<JobFiltersModalProps, "currentBreakpoint">
> = ({ modalOpen, filters }) => {
  const { observe, currentBreakpoint } = useDimensions<HTMLDivElement>({
    breakpoints: {
      XS: 0,
      MD: screenSizes.md,
      LG: screenSizes.lg,
      XL: screenSizes.xl,
    },
    updateOnBreakpointChange: true,
  })
  useEffect(() => {
    if (modalOpen) {
      document.body.classList.add("body-modal-open")
    } else {
      document.body.classList.remove("body-modal-open")
    }
    // Cleanup function to ensure class is removed if component is unmounted
    return () => {
      document.body.classList.remove("body-modal-open")
    }
  }, [modalOpen])
  return (
    <>
      <JobFiltersSlideIn
        modalOpen={modalOpen}
        filters={filters}
        currentBreakpoint={currentBreakpoint}
      />
      <ModalBackdrop modalOpen={modalOpen} observe={observe} />
    </>
  )
}

export default JobFiltersModal
